import type { Map } from 'mapbox-gl';
import { useEffect } from 'react';
import type { IDealList } from '@/types/api';
import { cameraDuration, DEAL_FOCUS_DURATION_MS, DEAL_FOCUS_ZOOM } from '../utils/cameraMotion';

interface PromotedLocationRedirectProps {
  /** The selected location id from `?location=<id>`, or `null` when absent. */
  locationId: string | null;
  /** The mirrored deals the shell already polls; undefined until the first read lands. */
  deals: IDealList | undefined;
  /** Selects the deal instead, which clears `?location=` in the same URL write. */
  onSelectDeal: (dealId: string) => void;
  map: React.RefObject<Map | null>;
  isMapLoaded: boolean;
}

/**
 * Resolves a `?location=` link that points at a location a deal was promoted from to that deal's
 * card. The link written on a deal in HubSpot carries the location it was created from, and the
 * backend keeps a location carrying a deal out of the marker set — so without this the link lands
 * on a pin the map no longer draws.
 *
 * The camera follows, which selecting a deal on the map deliberately does not: someone arriving
 * from HubSpot has no idea where on the map the deal sits, so the card alone would leave them
 * looking for its pin. An unplaced deal is opened where the camera already is.
 *
 * Side effects only, like the other map layer children.
 */
export function PromotedLocationRedirect({
  locationId,
  deals,
  onSelectDeal,
  map,
  isMapLoaded,
}: PromotedLocationRedirectProps) {
  useEffect(() => {
    if (locationId == null || deals == null) return;
    const promoted = deals.deals.find((deal) => deal.location_id === locationId);
    if (promoted == null) return;

    const { latitude, longitude } = promoted;
    if (isMapLoaded && map.current != null && latitude != null && longitude != null) {
      map.current.easeTo({
        center: [longitude, latitude],
        zoom: DEAL_FOCUS_ZOOM,
        duration: cameraDuration(DEAL_FOCUS_DURATION_MS),
      });
    }
    onSelectDeal(promoted.id);
  }, [locationId, deals, onSelectDeal, map, isMapLoaded]);

  return null;
}
